import {v4 as uuid} from 'uuid';

import * as API from '@pexip/infinity-api';
import type {Detach} from '@pexip/signal';
import {getErrorCode, withToken} from '@pexip/infinity-api';
import type {Bandwidth} from '@pexip/peer-connection';
import {createPCSignals} from '@pexip/peer-connection';
import {hasOwn} from '@pexip/utils';

import type {
    AllowMediaChangeOfDirection,
    BreakoutRoom,
    Call,
    CallSignals,
    CallUuid,
    Client,
    ConferenceFeatureFlags,
    ConferenceStatus,
    CurrentServiceType,
    DegradationPreference,
    DisconnectReason,
    EndpointResponse,
    ExtendedInfinityErrorMessage,
    GetEndpointParams,
    GetEndpointResponse,
    InfinityClient,
    InfinitySignals,
    MediaPriority,
    MessageEvent,
    NormalizedPresentationEvent,
    Participant,
    ParticipantID,
    RequestClient,
    RoomID,
    Sources,
    Stats,
    Stun,
    ThemeSchema,
    Turn,
} from './types';

import type {LogMethod} from './baseLogger';
import {ClientCallType} from './clientCallType';
import {createRequestClient} from './requestClient';
import {createCall} from './call';
import {createEventSourceManager, eventSignals} from './eventSource';
import {logger, redact} from './logger';
import {
    assertRoomID,
    assertParticipantID,
    isParticipantID,
    isRoomID,
} from './typeGuards';
import {
    APPLICATION_JSON,
    MAIN_ROOM_ID,
    NONE,
    SDP_OFFER_IGNORED,
    DEFAULT_CLIENT_CALLTYPE,
    DEFAULT_PERSONAL_MIX,
    DEFAULT_MIX,
} from './constants';
import {
    buildSsoTargetUrl,
    captureNoRequestClient,
    createMainStatsSignals,
    getBandwidth,
    isCriticalAction,
    matchAllowMediaChangeOfDirection,
    matchCallType,
    normalizeConferenceState,
    normalizeParticipant,
    normalizePresentationEvent,
    toClientCallType,
} from './utils';

interface Cache {
    rooms: RoomID[];
    breakoutRoomNames: Map<RoomID, string>;
}

type EventHandler<T> = (roomId: RoomID, participant: T) => void;

type Fetch = typeof window.fetch;
type Logger = {
    log: LogMethod;
    redact: (text: string) => string;
};
type CreateFetcher = (logger: Logger) => Fetch;

const debugLogger: Logger = {
    log: (meta, msg) => logger.debug(meta, msg),
    redact: (text: string) => logger.redact(text),
};
const infoLogger: Logger = {
    log: (meta, msg) => logger.info(meta, msg),
    redact: (text: string) => logger.redact(text),
};

/**
 * Fetch with built-in logging the request and response
 */
const createFetcherWithLogging: CreateFetcher = log => async (input, init) => {
    const url = input instanceof Request ? input.url : input;
    const urlStr = log.redact(url.toString());
    const {headers: _, ...initWithoutHeaders} = init ?? {};
    log.log({init: initWithoutHeaders}, `Request [${urlStr}]`);
    const response = await window.fetch(input, init);
    const statusInfo = {
        status: response.status,
        statusText: response.statusText,
    };
    if (
        response.headers.get('content-type')?.startsWith('application/json') &&
        response.status !== 204
    ) {
        try {
            const clonedResponse = response.clone();
            const body = (await clonedResponse.json()) as unknown;
            if (body && hasOwn(body, 'result')) {
                redact(body.result, 'token');
                redact(body.result, 'conference_name');
            }
            log.log({...statusInfo, body}, `Response [${urlStr}]`);
            // Replay the response
            return response;
        } catch {
            return response;
        }
    } else {
        log.log(statusInfo, `Response [${urlStr}]`);
        return response;
    }
};

const fetchWithDebugLog = createFetcherWithLogging(debugLogger);
const fetchWithInfoLog = createFetcherWithLogging(infoLogger);

const createParticipants = () => {
    const participants = new Map<RoomID, Map<ParticipantID, Participant>>([
        [MAIN_ROOM_ID, new Map()],
    ]);

    const get = (roomID: RoomID, uuid: ParticipantID) => {
        return participants.get(roomID)?.get(uuid);
    };
    const set = (roomID: RoomID, participant?: Participant) => {
        if (!participant) {
            participants.set(roomID, new Map());
            return;
        }
        const roomParticipants = participants.get(roomID);
        if (roomParticipants) {
            roomParticipants.set(participant.uuid, participant);
        } else {
            participants.set(
                roomID,
                new Map([[participant.uuid, participant]]),
            );
        }
    };

    const find = (
        roomId: RoomID,
        predicate: (participant: Participant) => boolean,
    ) => {
        const roomParticipants = participants.get(roomId);
        if (!roomParticipants) {
            return undefined;
        }
        for (const p of roomParticipants.values()) {
            if (predicate(p)) {
                return p;
            }
        }
        return undefined;
    };

    const remove = (roomID: RoomID, uuid?: ParticipantID) => {
        if (uuid) {
            return participants.get(roomID)?.delete(uuid);
        }
        return participants.delete(roomID);
    };
    const getAll = (roomID: RoomID) => {
        const list = participants.get(roomID);
        if (!list) {
            return [];
        }
        return Array.from(list.values());
    };
    return {
        get,
        set,
        get size() {
            return participants.size;
        },
        find,
        remove,
        getAll,
    };
};

export const createInfinityClient = (
    signals: InfinitySignals,
    callSignals: CallSignals,
): InfinityClient => {
    let requestClient: RequestClient | undefined;
    let eventSourceManager:
        | ReturnType<typeof createEventSourceManager>
        | undefined;
    let currentCall: Call | undefined;
    /**
     * Map to the `call_type` from the respond of `request_token`
     * @see {@link https://docs.pexip.com/api_client/api_rest.htm#request_token}
     */
    let currentAllowChangeOfMediaDirection: AllowMediaChangeOfDirection = {
        video: true,
        audio: true,
        presentation: true,
    };
    let currentCallType = DEFAULT_CLIENT_CALLTYPE;
    let currentCallUuid: CallUuid;
    let currentConferenceAlias: string;
    let currentConferenceName: string | undefined;
    let currentParticipantUuid: ParticipantID;
    let currentHost: string;
    let currentPin: string | undefined;
    let currentConferenceExtension: string | undefined;
    let currentChosenIdp = NONE;
    let currentSsoToken = NONE;
    let currentToken: string | undefined;
    let currentServiceType: CurrentServiceType | undefined;
    let currentCallTag: string | undefined;
    let currentBreakoutUuid: RoomID | undefined;
    let detachSignals: Detach[] = [];
    let participants = createParticipants();
    let conferenceStatus = new Map<RoomID, ConferenceStatus>();
    let conferenceFeatureFlags: ConferenceFeatureFlags | undefined;
    let secureCheckCode = '';
    let stun: Stun;
    let turn: Turn;
    let dataChannelId: number | undefined;
    let latestStats: Stats | undefined;
    let useRelayCandidatesOnly: boolean | undefined;
    let disconnectPromise: ReturnType<typeof disconnectRequest> | undefined;
    let restartCallPromise: ReturnType<typeof restartCallRequest> | undefined;
    let themeSchema: ThemeSchema | undefined;
    let breakoutRooms = new Map<RoomID, BreakoutRoom>();
    const presentationCache = new Map<RoomID, NormalizedPresentationEvent>();
    let bandwidthIn: number | undefined;
    let bandwidthOut: number | undefined;
    let feccSupported = false;
    const participantSync = new Map<RoomID, boolean>();
    let cache: Partial<Cache> = {};

    let retryList: Array<() => Promise<EndpointResponse>> = [];

    const timerIDs = {
        eventSrcReconnect: -1,
        update: -1,
        pushStatistics: -1,
    };

    const getCurrentRoomId = () => {
        return currentBreakoutUuid || MAIN_ROOM_ID;
    };

    /**
     * In a gateway call we only have 2 participants so we are interested in the remote
     */
    const gatewayParticipantCanFecc = () => {
        if (currentServiceType !== 'gateway' && participants.size !== 2) {
            return false;
        }
        return Boolean(
            participants.find(
                getCurrentRoomId(),
                participant => participant.uuid !== currentParticipantUuid,
            )?.canFecc,
        );
    };

    const setPushStatisticsTimer = () => {
        if (requestClient?.statsUpdateInterval) {
            timerIDs.pushStatistics = window.setTimeout(
                pushStatistics,
                requestClient.statsUpdateInterval,
            );
        }
    };

    const getCurrentCallUuid = () => currentCallUuid;
    const getBreakoutParticipantUuid = (breakoutUuid: string | undefined) => {
        if (isRoomID(breakoutUuid)) {
            return breakoutRooms.get(breakoutUuid)?.participant_uuid;
        }
    };

    const setServiceType = (serviceType: CurrentServiceType) => {
        currentServiceType = serviceType;
        signals.onServiceType.emit(currentServiceType);
    };

    type GenerateEndpointParams<T extends keyof Client> =
        | {
              func: (
                  reqParams: GetEndpointParams<T>,
              ) => Promise<GetEndpointResponse<T>>;
              funcName: keyof Client;
              retriable?: boolean;
              fetcher: Fetch;
              requiresToken: false;
          }
        | {
              func: (
                  reqParams: GetEndpointParams<T>,
                  fetcher: Fetch,
              ) => Promise<GetEndpointResponse<T>>;
              funcName: T;
              retriable?: boolean;
              fetcher: Fetch;
              requiresToken: true;
          };
    function generateEndpoint<T extends keyof Client>(
        opts: GenerateEndpointParams<T>,
    ) {
        const wrappedFunc = async (...args: GetEndpointParams<T>) => {
            try {
                for (const arg of args) {
                    redact(arg, 'token');
                    redact(arg, 'ssoToken');
                }
            } catch (e) {
                logger.error(
                    {error: e},
                    'Error redacting sensitive information from request',
                );
            }
            const {func, fetcher, funcName, requiresToken, retriable} = opts;

            const flushRetryQueue = async () => {
                /**
                 * Although it is generally not a great idea to make retries
                 * dependent on another request being successful as it might
                 * never happen.
                 * Because of our unique context (video as main feature) we
                 * should always try to renegotiate the call on network changes.
                 * Without A/V the app effectively has no use.
                 * This renegotiation after network changes is therefore a
                 * reliable proxy for network availability as long as it is
                 * implemented.
                 */
                if (
                    retryList.length > 0 &&
                    (await requestClient?.refreshToken())
                ) {
                    const promise = Promise.allSettled(
                        retryList.map(func => func()),
                    );
                    retryList = [];
                    const res = await promise;
                    const hasFailure = res.find(
                        settled =>
                            settled.status === 'fulfilled' &&
                            settled.value === undefined,
                    );
                    if (!hasFailure) {
                        signals.onRetryQueueFlushed.emit();
                    }
                }
            };

            let endpointPromise: Awaited<GetEndpointResponse<T>> | undefined;
            try {
                if (requiresToken) {
                    if (!requestClient) {
                        captureNoRequestClient();
                        return;
                    }
                    await flushRetryQueue();
                    endpointPromise = await func(args, (input, init) => {
                        return withToken(fetcher, requestClient?.token ?? '')(
                            input,
                            init,
                        );
                    });
                } else {
                    endpointPromise = await func(args);
                }
            } catch (e) {
                if (retriable) {
                    // if anything fails again it will be added back to the queue. Order is lost
                    logger.warn(
                        {error: e},
                        `Adding failed request '${funcName}' to retry queue.`,
                    );
                    retryList.push(() => wrappedFunc(...args));
                } else {
                    logger.error(
                        {error: e},
                        `Request '${funcName}' threw an Error.`,
                    );
                    if (isCriticalAction(funcName)) {
                        cleanupAndDisconnect(
                            'Could not execute critical network action',
                        );
                        return endpointPromise;
                    }
                }
                signals.onFailedRequest.emit(funcName);
                throw e;
            }
            return endpointPromise;
        };
        return wrappedFunc;
    }

    const startCall = ({
        bandwidth,
        maxFrameSize,
        allowVP9,
        mediaStream,
        callType = DEFAULT_CLIENT_CALLTYPE,
        networkPriorities,
        encodingPriorities,
        degradationPreference,
        allowChangeOfMediaDirection,
    }: Pick<GetEndpointParams<'call'>[0], 'mediaStream'> & {
        bandwidth: Bandwidth;
        maxFrameSize?: number;
        allowVP9?: boolean;
        callType?: ClientCallType;
        /**
         * DSCP markings for each media line
         * This has the same effect as priority, except that it only affects
         * the DSCP markings of the generated packets, as described in
         * [rfc8835] section 4.2.
         */
        networkPriorities?: MediaPriority;
        /**
         * Encoding priority for each media line
         * Indicates the priority of a RTCRtpSender, which influences the
         * bandwidth allocation among RTCRtpSender objects. It is specified in
         * [rfc8835], Section 4. The user agent is free to sub-allocate
         * bandwidth between the encodings of a RTCRtpSender.
         */
        encodingPriorities?: MediaPriority;
        degradationPreference?: DegradationPreference;
        /**
         * Allow the call to change of Transceiver direction on-demand.
         *
         * @defaultValue `true`
         */
        allowChangeOfMediaDirection?: AllowMediaChangeOfDirection;
    }) => {
        currentCallType = matchCallType(
            callType,
            toClientCallType(conferenceFeatureFlags?.callType),
        );
        currentAllowChangeOfMediaDirection = matchAllowMediaChangeOfDirection(
            allowChangeOfMediaDirection ?? currentAllowChangeOfMediaDirection,
            conferenceFeatureFlags?.callType,
        );
        logger.info(
            {
                callType: ClientCallType[callType],
                allowChangeOfMediaDirection: currentAllowChangeOfMediaDirection,
                bandwidth,
                mediaStream,
            },
            'starting call with CallType',
        );
        currentCall = createCall({
            sendOffer,
            ack,
            newCandidate,
            update,
            takeFloor,
            releaseFloor,
            getCurrentCallUuid,
            signals,
            eventSignals,
            callSignals,
            networkPriorities,
            encodingPriorities,
            degradationPreference,
            peerOptions: {
                allow1080p: conferenceFeatureFlags?.allow1080p,
                allow4kPreso: conferenceFeatureFlags?.isDirectMedia,
                allowCodecSdpMunging: !conferenceFeatureFlags?.isDirectMedia,
                allowVP9: conferenceFeatureFlags?.allowVP9 && allowVP9,
                allowH264: conferenceFeatureFlags?.allowH264,
                bandwidth,
                maxFrameSize,
                rtcConfig: {
                    bundlePolicy: 'max-bundle',
                    iceServers: [
                        ...(stun
                            ? [
                                  {
                                      urls: stun.map(stun => stun.url),
                                  },
                              ]
                            : []),
                        ...(turn ? turn : []),
                    ],
                    ...(useRelayCandidatesOnly && {
                        iceTransportPolicy: 'relay',
                    }),
                },
            },
            dataChannelId,
            mediaStream:
                typeof mediaStream === 'function' ? mediaStream() : mediaStream,
            isDirectMedia: Boolean(conferenceFeatureFlags?.isDirectMedia),
            callType: currentCallType,
            allowChangeOfMediaDirection: currentAllowChangeOfMediaDirection,
            pcMainSignals: createPCSignals(
                [
                    'onConnectionStateChange',
                    'onIceCandidate',
                    'onIceConnectionStateChange',
                    'onReceiveIceCandidate',
                    'onRemoteStreams',
                    'onSecureCheckCode',
                    'onSignalingStateChange',
                    'onTransceiverChange',
                ],
                'call:pc:main',
            ),
            mainStatsSignals: createMainStatsSignals(),
        });
    };

    const restartCallRequest = async ({
        bandwidth,
        ...opt
    }: Parameters<InfinityClient['restartCall']>[0]) => {
        await disconnectRequest({
            /**
             * There is a specific case for direct media call when
             * one peer disconnects we need to restart Webrtc call
             * and wait for another participant to arrive.
             * So we need to keep EventSource open to wait for the
             * ping from MCU for that.
             */
            callback: async () => {
                if (
                    currentCall?.presoState.send === 'connected' ||
                    currentCall?.presoState.send === 'connecting'
                ) {
                    await currentCall?.stopPresenting();
                }
                currentCall?.disconnect();
                currentCall = undefined;
                currentCallUuid = undefined;
            },
            // Reuse existing token for this usecase
            release: () => {
                return Promise.resolve();
            },
        });
        startCall({
            ...opt,
            bandwidth: {
                in: getBandwidth(bandwidth, bandwidthIn),
                out: getBandwidth(bandwidth, bandwidthOut),
            },
        });
    };

    const restartCall = async (
        ...params: Parameters<typeof restartCallRequest>
    ) => {
        restartCallPromise = restartCallRequest(...params);
        return restartCallPromise;
    };

    const emitErrorSignal = (error: string) => {
        signals.onError.emit({
            error: error as ExtendedInfinityErrorMessage,
            errorCode: getErrorCode(error),
        });
    };

    const call = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'call'>) => {
            const {
                bandwidth,
                conferenceAlias,
                directMedia = true,
                displayName,
                node = window.location.host,
                host = `${window.location.protocol}//${node}`,
                mediaStream,
                pin,
                chosenIdp,
                ssoToken,
                token,
                conferenceExtension,
                callTag,
                allowChangeOfMediaDirection,
                callType,
                clientId,
                networkPriorities,
                encodingPriorities,
                degradationPreference,
                supportsDirectChat = false,
                allowH264 = true,
                isMeetingManager = false,
                allowVP9 = true,
                maxFrameSize,
            } = reqParams[0];

            if (restartCallPromise) {
                await restartCallPromise;
            }
            if (disconnectPromise) {
                await disconnectPromise;
            }

            const pinNow = pin ?? currentPin;
            const chosenIdpNow = chosenIdp ?? currentChosenIdp;
            const ssoTokenNow = ssoToken ?? currentSsoToken;
            const tokenNow = token ?? currentToken;
            const conferenceExtensionNow =
                conferenceExtension ?? currentConferenceExtension;
            const callTagNow = callTag ?? currentCallTag;

            // Build SSO redirect target URL if conferenceAlias is provided
            let sso_redirect_target: string | undefined;

            if (conferenceAlias) {
                logger.addRedactPattern(conferenceAlias);
                sso_redirect_target = buildSsoTargetUrl({
                    conferenceAlias,
                    displayName,
                    callTag: callTagNow,
                    callType,
                    isMeetingManager,
                });
            }

            const res = await API.requestToken({
                fetcher: pinNow
                    ? API.withPin(fetchWithInfoLog, pinNow)
                    : fetchWithInfoLog,
                body: {
                    display_name: displayName,
                    chosen_idp: chosenIdpNow,
                    direct_media: directMedia,
                    sso_token: ssoTokenNow,
                    sso_redirect_target,
                    node,
                    token: tokenNow,
                    conference_extension: conferenceExtensionNow,
                    call_tag: callTagNow,
                    client_id: clientId,
                    supports_direct_chat: supportsDirectChat,
                },
                params: {
                    conferenceAlias,
                },
                host,
            });

            switch (res.status) {
                case 200:
                    if (pin) {
                        currentPin = pin;
                    }
                    if (chosenIdp) {
                        currentChosenIdp = chosenIdp;
                    }
                    if (ssoToken) {
                        currentSsoToken = ssoToken;
                    }
                    if (conferenceExtension) {
                        currentConferenceExtension = conferenceExtension;
                    }
                    if (callTag) {
                        currentCallTag = callTag;
                    }
                    conferenceFeatureFlags = {
                        chatEnabled: Boolean(res.data.result.chat_enabled),
                        isDirectMedia: Boolean(res.data.result.direct_media),
                        guestsCanPresent: Boolean(
                            res.data.result.guests_can_present,
                        ),
                        allow1080p: Boolean(res.data.result.allow_1080p),
                        allowVP9: Boolean(res.data.result.vp9_enabled),
                        allowH264,
                        callType: res.data.result.call_type ?? 'video',
                        breakoutRoomsEnabled: Boolean(
                            res.data.result.breakout_rooms,
                        ),
                        maxFrameSize,
                    };
                    currentConferenceName = res.data.result.conference_name;
                    // The eventual call type is limited by the VMR call type setting
                    currentCallType = matchCallType(
                        callType,
                        toClientCallType(conferenceFeatureFlags.callType),
                    );
                    currentAllowChangeOfMediaDirection =
                        matchAllowMediaChangeOfDirection(
                            allowChangeOfMediaDirection ??
                                currentAllowChangeOfMediaDirection,
                            conferenceFeatureFlags.callType,
                        );
                    currentConferenceAlias = conferenceAlias;
                    if (isParticipantID(res.data.result.participant_uuid)) {
                        currentParticipantUuid =
                            res.data.result.participant_uuid;
                    }
                    currentHost = host;

                    setServiceType(res.data.result.current_service_type);

                    requestClient = createRequestClient({
                        fetcher: fetchWithInfoLog,
                        conferenceAlias,
                        token: res.data.result.token,
                        expires: Number(res.data.result.expires),
                        host,
                        clientStatsUpdateInterval:
                            res.data.result.client_stats_update_interval,
                        tokenExpiredCb: () => {
                            cleanupAndDisconnect(
                                'Timer expired awaiting token refresh',
                            );
                        },
                        tokenRefreshedCb: token => {
                            signals.onTokenRefreshed.emit({token});
                        },
                    });
                    themeSchema = await requestTheme({});
                    if (detachSignals.length > 0) {
                        cleanupSignals();
                    }
                    eventSourceManager =
                        createEventSourceManager(requestClient);
                    await eventSourceManager.connect(host, conferenceAlias);
                    detachSignals = subscribeSignals();

                    stun = res.data.result.stun;
                    turn = res.data.result.turn;
                    dataChannelId = res.data.result.pex_datachannel_id;
                    useRelayCandidatesOnly =
                        res.data.result.use_relay_candidates_only;
                    bandwidthOut = res.data.result.bandwidth_out;
                    bandwidthIn = res.data.result.bandwidth_in;

                    setPushStatisticsTimer();

                    // Signal Subscriptions are deferred until the EventSource is connected
                    // Thus we need to mimic the `onConnected` event manually
                    signals.onConnected.emit();
                    signals.onAuthenticatedWithConference.emit({
                        conferenceAlias: currentConferenceAlias,
                        conferenceName: currentConferenceName,
                    });
                    logger.debug({currentCall}, 'Creates a new call');
                    startCall({
                        bandwidth: {
                            in: getBandwidth(bandwidth, bandwidthIn),
                            out: getBandwidth(bandwidth, bandwidthOut),
                        },
                        mediaStream,
                        callType: currentCallType,
                        allowChangeOfMediaDirection:
                            currentAllowChangeOfMediaDirection,
                        networkPriorities,
                        encodingPriorities,
                        degradationPreference,
                        allowVP9,
                        maxFrameSize,
                    });

                    break;

                case 403:
                case 415:
                    // For this response string is likely considered as error
                    if (typeof res.data.result === 'string') {
                        emitErrorSignal(res.data.result);
                    } else if (
                        'pin' in res.data.result &&
                        'guest_pin' in res.data.result
                    ) {
                        if (conferenceExtension) {
                            currentConferenceExtension = conferenceExtension;
                        }
                        signals.onPinRequired.emit({
                            hasHostPin: res.data.result.pin === 'required',
                            hasGuestPin:
                                res.data.result.guest_pin === 'required',
                        });
                    } else if ('idp' in res.data.result) {
                        if (pin) {
                            currentPin = pin;
                        }
                        const idps = res.data.result.idp.map(
                            ({disable_popup_flow, ...rest}) => ({
                                disablePopupFlow: disable_popup_flow,
                                ...rest,
                            }),
                        );
                        signals.onIdp.emit(idps);
                    } else if (
                        'redirect_url' in res.data.result &&
                        'redirect_idp' in res.data.result
                    ) {
                        signals.onRedirect.emit({
                            redirectUrl: res.data.result.redirect_url,
                            redirectIdp: res.data.result.redirect_idp,
                            disablePopupFlow:
                                res.data.result.disable_popup_flow,
                        });
                    } else if (
                        'conference_extension' in res.data.result &&
                        'conference_extension_type' in res.data.result
                    ) {
                        signals.onExtension.emit(
                            res.data.result.conference_extension_type,
                        );
                    } else if (res.data.result.display_name === 'required') {
                        if (pin) {
                            currentPin = pin;
                        }
                        signals.onDisplayNameRequired.emit();
                    }
                    break;
                case 404:
                    emitErrorSignal(res.data.result);
                    break;
                case 502:
                case 503:
                case 504:
                case 529:
                    emitErrorSignal(res.data.result);
                    break;
            }
            return res;
        },
        funcName: 'call',
        fetcher: fetchWithInfoLog,
        requiresToken: false,
    });

    const pushStatistics = () => {
        const convertBitrate = (bitrate?: number) =>
            bitrate ? bitrate / 1000 : undefined;
        const convertJitter = (jitter?: number) =>
            jitter ? jitter * 1000 : undefined;
        const convertPercentage = (percentage?: number) =>
            percentage ? percentage * 100 : undefined;

        void statistics({
            audio: {
                rx_bitrate: convertBitrate(latestStats?.inbound.audio?.bitrate),
                rx_codec: latestStats?.inbound.audio?.codec?.replace(
                    'audio/',
                    '',
                ),
                rx_historic_packet_loss: convertPercentage(
                    latestStats?.inbound.audio?.totalPercentageLost,
                ),
                rx_jitter: convertJitter(latestStats?.inbound.audio?.jitter),
                rx_packets_lost: latestStats?.inbound.audio?.packetsLost,
                rx_packets_received:
                    latestStats?.inbound.audio?.packetsTransmitted,
                rx_windowed_packet_loss: convertPercentage(
                    latestStats?.inbound.audio?.recentPercentageLost,
                ),
                tx_bitrate: convertBitrate(
                    latestStats?.outbound.audio?.bitrate,
                ),
                tx_codec: latestStats?.outbound.audio?.codec?.replace(
                    'audio/',
                    '',
                ),
                tx_historic_packet_loss: convertPercentage(
                    latestStats?.outbound.audio?.totalPercentageLost,
                ),
                tx_packets_sent:
                    latestStats?.outbound.audio?.packetsTransmitted,
                tx_rb_jitter: convertJitter(
                    latestStats?.outbound.audio?.jitter,
                ),
                tx_rb_packetslost: latestStats?.outbound.audio?.packetsLost,
                tx_windowed_packet_loss: convertPercentage(
                    latestStats?.outbound.audio?.recentPercentageLost,
                ),
            },
            video: {
                rx_bitrate: convertBitrate(latestStats?.inbound.video?.bitrate),
                rx_codec: latestStats?.inbound.video?.codec?.replace(
                    'video/',
                    '',
                ),
                rx_fps: latestStats?.inbound.video?.framesPerSecond,
                rx_historic_packet_loss: convertPercentage(
                    latestStats?.inbound.video?.totalPercentageLost,
                ),
                rx_packets_lost: latestStats?.inbound.video?.packetsLost,
                rx_packets_received:
                    latestStats?.inbound.video?.packetsTransmitted,
                rx_resolution: latestStats?.inbound.video?.resolution,
                rx_windowed_packet_loss: convertPercentage(
                    latestStats?.inbound.video?.recentPercentageLost,
                ),
                tx_bitrate: convertBitrate(
                    latestStats?.outbound.video?.bitrate,
                ),
                tx_codec: latestStats?.outbound.video?.codec?.replace(
                    'video/',
                    '',
                ),
                tx_fps: latestStats?.outbound.video?.framesPerSecond,
                tx_historic_packet_loss: convertPercentage(
                    latestStats?.outbound.video?.totalPercentageLost,
                ),
                tx_packets_sent:
                    latestStats?.outbound.video?.packetsTransmitted,
                tx_rb_packetslost: latestStats?.outbound.video?.packetsLost,
                tx_resolution: latestStats?.outbound.video?.resolution,
                tx_windowed_packet_loss: convertPercentage(
                    latestStats?.outbound.video?.recentPercentageLost,
                ),
            },
            presentation: {
                rx_bitrate: convertBitrate(latestStats?.inbound.preso?.bitrate),
                rx_codec: latestStats?.inbound.preso?.codec?.replace(
                    'video/',
                    '',
                ),
                rx_fps: latestStats?.inbound.preso?.framesPerSecond,
                rx_historic_packet_loss: convertPercentage(
                    latestStats?.inbound.preso?.totalPercentageLost,
                ),
                rx_packets_lost: latestStats?.inbound.preso?.packetsLost,
                rx_packets_received:
                    latestStats?.inbound.preso?.packetsTransmitted,
                rx_resolution: latestStats?.inbound.preso?.resolution,
                rx_windowed_packet_loss: convertPercentage(
                    latestStats?.inbound.preso?.recentPercentageLost,
                ),
                tx_bitrate: convertBitrate(
                    latestStats?.outbound.preso?.bitrate,
                ),
                tx_codec: latestStats?.outbound.preso?.codec?.replace(
                    'video/',
                    '',
                ),
                tx_fps: latestStats?.outbound.preso?.framesPerSecond,
                tx_historic_packet_loss: convertPercentage(
                    latestStats?.outbound.preso?.totalPercentageLost,
                ),
                tx_packets_sent:
                    latestStats?.outbound.preso?.packetsTransmitted,
                tx_rb_packetslost: latestStats?.outbound.preso?.packetsLost,
                tx_resolution: latestStats?.outbound.preso?.resolution,
                tx_windowed_packet_loss: convertPercentage(
                    latestStats?.outbound.preso?.recentPercentageLost,
                ),
            },
        });
        setPushStatisticsTimer();
    };

    const sendOffer = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'sendOffer'>, fetcher) => {
            const {
                sdp,
                conferenceAlias = currentConferenceAlias,
                breakoutUuid = currentBreakoutUuid,
                host = currentHost,
                participantUuid = currentParticipantUuid,
            } = reqParams[0];
            const breakoutParticipantUuid =
                getBreakoutParticipantUuid(breakoutUuid);

            const res =
                breakoutUuid && breakoutParticipantUuid
                    ? await API.breakoutCallsWebrtcParticipant({
                          fetcher,
                          body: {
                              call_type: 'WEBRTC',
                              sdp,
                              media_type: conferenceFeatureFlags?.callType,
                              fecc_supported: feccSupported,
                          },
                          params: {
                              conferenceAlias,
                              participantUuid: breakoutParticipantUuid,
                              breakoutUuid,
                          },
                          host,
                      })
                    : await API.callsWebrtcParticipant({
                          fetcher,
                          body: {
                              call_type: 'WEBRTC',
                              sdp,
                              media_type: conferenceFeatureFlags?.callType,
                              fecc_supported: feccSupported,
                          },
                          params: {
                              conferenceAlias,
                              participantUuid,
                          },
                          host,
                      });

            if (res.status === 200) {
                currentCallUuid = res.data.result.call_uuid;
                signals.onAnswer.emit(res.data.result);
            } else if (res.status === 403) {
                emitErrorSignal(res.data.result);
            }

            return res;
        },
        funcName: 'sendOffer',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const ack = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'ack'>, fetcher) => {
            const {
                callUuid = currentCallUuid,
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                participantUuid = currentParticipantUuid,
                breakoutUuid = currentBreakoutUuid,
                sdp,
                offerIgnored,
            } = reqParams[0];

            if (!callUuid) {
                return;
            }

            const breakoutParticipantUuid =
                getBreakoutParticipantUuid(breakoutUuid);

            if (breakoutUuid && breakoutParticipantUuid) {
                return API.breakoutAck({
                    fetcher,
                    params: {
                        conferenceAlias,
                        participantUuid: breakoutParticipantUuid,
                        breakoutUuid,
                        callUuid,
                    },
                    body: {
                        sdp: offerIgnored ? SDP_OFFER_IGNORED : sdp,
                        offer_ignored: offerIgnored,
                    },
                    host,
                });
            }

            return API.ack({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                    callUuid,
                },
                body: {
                    sdp: offerIgnored ? SDP_OFFER_IGNORED : sdp,
                    offer_ignored: offerIgnored,
                },
                host,
            });
        },
        funcName: 'ack',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const newCandidate = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'newCandidate'>, fetcher) => {
            const {
                candidate,
                callUuid = currentCallUuid,
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                participantUuid = currentParticipantUuid,
                breakoutUuid = currentBreakoutUuid,
            } = reqParams[0];

            if (!callUuid) {
                return;
            }

            const breakoutParticipantUuid =
                getBreakoutParticipantUuid(breakoutUuid);

            if (breakoutUuid && breakoutParticipantUuid) {
                return API.breakoutNewCandidate({
                    fetcher,
                    params: {
                        conferenceAlias,
                        participantUuid: breakoutParticipantUuid,
                        callUuid,
                        breakoutUuid,
                    },
                    body: candidate,
                    host,
                });
            }

            return API.newCandidate({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                    callUuid,
                },
                body: candidate,
                host,
            });
        },
        funcName: 'newCandidate',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const update = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'update'>, fetcher) => {
            const {
                sdp,
                callUuid = currentCallUuid,
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                participantUuid = currentParticipantUuid,
                breakoutUuid = currentBreakoutUuid,
                abortSignal,
            } = reqParams[0];

            const doUpdate = async () => {
                if (!callUuid) {
                    clearTimeout(timerIDs.update);
                    return;
                }

                try {
                    const breakoutParticipantUuid =
                        getBreakoutParticipantUuid(breakoutUuid);

                    const res =
                        breakoutUuid && breakoutParticipantUuid
                            ? await API.breakoutUpdate({
                                  fetcher,
                                  body: {
                                      sdp,
                                      fecc_supported: feccSupported,
                                  },
                                  params: {
                                      conferenceAlias,
                                      participantUuid: breakoutParticipantUuid,
                                      breakoutUuid,
                                      callUuid,
                                  },
                                  host,
                                  init: {signal: abortSignal},
                              })
                            : await API.update({
                                  fetcher,
                                  body: {
                                      sdp,
                                      fecc_supported: feccSupported,
                                  },
                                  params: {
                                      conferenceAlias,
                                      participantUuid,
                                      callUuid,
                                  },
                                  host,
                                  init: {signal: abortSignal},
                              });

                    if (res.status === 200) {
                        clearTimeout(timerIDs.update);
                        // aggressively refresh token and reconnect event source after successful update
                        // as it could be a result of connectivity issues.
                        // This works around the fact that backoff retry could be too big at this point and cause us to expire the token
                        await requestClient?.refreshToken();
                        if (
                            !present &&
                            eventSourceManager?.eventSource?.readyState === 2
                        ) {
                            void eventSourceManager.connect(
                                host,
                                conferenceAlias,
                            );
                        }

                        if (typeof res.data.result === 'string') {
                            res.data.result &&
                                signals.onAnswer.emit({
                                    sdp: res.data.result,
                                    call_uuid: callUuid,
                                });
                        } else if (res.data.result.sdp) {
                            signals.onAnswer.emit({
                                sdp: res.data.result.sdp,
                                call_uuid: res.data.result.call_uuid,
                            });
                        } else {
                            signals.onAnswer.emit(res.data.result);
                        }
                    }
                    return res;
                } catch (error) {
                    if (error instanceof Error && error.name !== 'AbortError') {
                        logger.error({error}, 'Failed /update attempt');
                        timerIDs.update = window.setTimeout(() => {
                            void doUpdate();
                        }, 1000);
                    }
                }
            };

            clearTimeout(timerIDs.update);
            return doUpdate();
        },
        funcName: 'update',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const takeFloor = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'takeFloor'>, fetcher) => {
            const {
                participantUuid = currentParticipantUuid,
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                breakoutUuid = currentBreakoutUuid,
            } = reqParams[0];

            const breakoutParticipantUuid =
                getBreakoutParticipantUuid(breakoutUuid);

            return breakoutUuid && breakoutParticipantUuid
                ? API.breakoutTakeFloor({
                      fetcher,
                      params: {
                          conferenceAlias,
                          participantUuid: breakoutParticipantUuid,
                          breakoutUuid,
                      },
                      host,
                  })
                : API.takeFloor({
                      fetcher,
                      params: {
                          conferenceAlias,
                          participantUuid,
                      },
                      host,
                  });
        },
        funcName: 'takeFloor',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const releaseFloor = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'releaseFloor'>, fetcher) => {
            const {
                participantUuid = currentParticipantUuid,
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                breakoutUuid = currentBreakoutUuid,
            } = reqParams[0];

            const breakoutParticipantUuid =
                getBreakoutParticipantUuid(breakoutUuid);

            return breakoutUuid && breakoutParticipantUuid
                ? API.breakoutReleaseFloor({
                      fetcher,
                      params: {
                          conferenceAlias,
                          participantUuid: breakoutParticipantUuid,
                          breakoutUuid,
                      },
                      host,
                  })
                : API.releaseFloor({
                      fetcher,
                      params: {
                          conferenceAlias,
                          participantUuid,
                      },
                      host,
                  });
        },
        funcName: 'releaseFloor',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
        retriable: true,
    });

    const breakoutRequestGuestToken = generateEndpoint({
        func: async (
            reqParams: GetEndpointParams<'breakoutRequestGuestToken'>,
            fetcher,
        ) => {
            const {
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                breakoutUuid = currentBreakoutUuid,
            } = reqParams[0];

            if (!breakoutUuid) {
                throw new Error('Missing breakoutUuid');
            }

            return API.breakoutRequestGuestToken({
                fetcher,
                params: {
                    conferenceAlias,
                    breakoutUuid,
                },
                host,
            });
        },
        funcName: 'breakoutRequestGuestToken',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
        retriable: true,
    });

    const cleanupSignals = () => {
        for (const d of detachSignals) {
            d();
        }
        detachSignals = [];
        signals.cancel();
    };

    const cleanup = () => {
        logger.debug({roomID: getCurrentRoomId()}, 'Cleanup');
        cache = {};
        currentCall?.disconnect();
        currentCall = undefined;
        eventSourceManager?.close();
        eventSourceManager = undefined;
        currentCallUuid = undefined;
        currentBreakoutUuid = undefined;
        dataChannelId = undefined;
        latestStats = undefined;
        useRelayCandidatesOnly = undefined;
        participants = createParticipants();
        breakoutRooms = new Map();
        secureCheckCode = '';
        conferenceStatus = new Map();
        currentPin = undefined;
        currentChosenIdp = NONE;
        currentSsoToken = NONE;
        currentToken = undefined;
        currentConferenceExtension = undefined;
        currentCallTag = undefined;
        retryList = [];
        cleanupSignals();
        Object.values(timerIDs).map(timer => clearTimeout(timer));
    };

    const releaseToken = async (reason?: DisconnectReason) => {
        await requestClient?.cleanup(reason);
        requestClient = undefined;
    };

    const cleanupAndDisconnect = (error: ExtendedInfinityErrorMessage) => {
        signals.onDisconnected.emit({
            error,
            errorCode: getErrorCode(error), // Match API error or get a default errorCode
        });
        cleanup();
        void releaseToken(error);
    };

    const disconnectRequest = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'disconnect'>, fetcher) => {
            const {
                callUuid = currentCallUuid,
                conferenceAlias = currentConferenceAlias,
                breakoutUuid = currentBreakoutUuid,
                host = currentHost,
                participantUuid = currentParticipantUuid,
                reason,
                callback,
                release = releaseToken,
            } = reqParams[0];

            if (callback) {
                await callback();
            } else {
                cleanup();
            }

            const breakoutParticipantUuid =
                getBreakoutParticipantUuid(breakoutUuid);

            if (callUuid && breakoutUuid && breakoutParticipantUuid) {
                await API.breakoutDisconnectCall({
                    fetcher,
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                        callUuid,
                        participantUuid: breakoutParticipantUuid,
                    },
                    host,
                });
                return;
            }

            if (callUuid && reason !== 'Browser closed') {
                try {
                    await API.disconnectCall({
                        fetcher,
                        params: {
                            conferenceAlias,
                            participantUuid,
                            callUuid,
                        },
                        host,
                    });
                } catch (reason) {
                    logger.warn({reason}, 'Unable to disconnect a call');
                }
            }

            await release(reason);
        },
        funcName: 'disconnect',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const disconnect = async (
        ...params: Parameters<typeof disconnectRequest>
    ) => {
        if (restartCallPromise) {
            await restartCallPromise;
        }
        disconnectPromise = disconnectRequest(...params);
        return disconnectPromise;
    };

    const handleParticipantsSyncEnded: EventHandler<void> = id => {
        logger.debug({roomID: id}, '[Participant] Sync End');
        signals.onParticipants.emit({
            id,
            participants: participants.getAll(id),
        });
    };

    const handleParticipantCreate: EventHandler<Participant> = (
        id,
        participant,
    ) => {
        logger.debug({roomID: id, participant}, '[Participant] Create');
        const isCurrentParticipant =
            participant.uuid ===
            (id === MAIN_ROOM_ID
                ? currentParticipantUuid
                : getBreakoutParticipantUuid(id));

        if (isCurrentParticipant && participant.serviceType) {
            setServiceType(participant.serviceType);
        }
        participants.set(id, participant);

        // Only emit onParticipantJoined Signal when it is NOT doing
        // `participant_sync`
        if (!participantSync.get(id)) {
            signals.onParticipantJoined.emit({id, participant});
        }

        /*
         * We would like the following code to only be executed when the current participant first joins.
         * We were assuming that if the current participant was created outside the participant sync events that
         * it was when they first joined but Greg explained that this is not always true:
         * "If we get a roster tick which has the new user before we get the eventsource and start sending
         * the data then the new user will be part of the "current roster" data and hence be inside the
         * sync begin/end. If the roster tick which includes the new user happens later,
         * it wont be in the current roster and will come along later outside the sync messages."
         *
         * Therefore we need to execute this code even if current participant participant_create event was part of
         * sync or not.
         */
        if (isCurrentParticipant) {
            signals.onMe.emit({id, participant});
            if (participant.canMute) {
                signals.onMyselfMuted.emit({
                    isServerMuted: participant.isMuted,
                    hasServerMuteStateChanged: false,
                });
            }
        }
    };

    const handleParticipantUpdate: EventHandler<Participant> = (
        id,
        participant,
    ) => {
        // Skip when there is no such participant
        const existingParticipant = participants.get(id, participant.uuid);
        if (!existingParticipant) {
            return;
        }
        logger.debug({roomID: id, participant}, '[Participant] Update');
        const isCurrentParticipant =
            participant.uuid ===
            (id === MAIN_ROOM_ID
                ? currentParticipantUuid
                : getBreakoutParticipantUuid(id));

        if (isCurrentParticipant) {
            const muteChanged =
                participant.isMuted !==
                participants.get(id, participant.uuid)?.isMuted;

            if (participant.canMute && muteChanged) {
                signals.onMyselfMuted.emit({
                    isServerMuted: participant.isMuted,
                    hasServerMuteStateChanged: true,
                });
            }

            if (participant.serviceType) {
                setServiceType(participant.serviceType);
            }
        } else if (participant.raisedHand !== existingParticipant.raisedHand) {
            signals.onRaiseHand.emit({id, participant});
        }

        participants.set(id, participant);

        if (isCurrentParticipant) {
            signals.onMe.emit({id, participant});
        }
        signals.onParticipantUpdated.emit({id, participant});
    };

    const handleParticipantDelete: EventHandler<Participant> = (
        id,
        participant,
    ) => {
        if (!participants.remove(id, participant.uuid)) {
            return;
        }
        logger.debug({roomID: id, participant}, '[Participant] Delete');
        signals.onParticipantLeft.emit({
            id,
            participant,
        });
    };

    const handleOnMessage: EventHandler<MessageEvent> = (
        roomId,
        {type, uuid: userId, payload: message, direct, origin},
    ) => {
        const meta = {
            at: new Date(),
            id: uuid(),
            roomId,
            displayName:
                (userId &&
                    participants.get(MAIN_ROOM_ID, userId)?.displayName) ||
                origin,
            userId,
            direct,
        };

        const [payloadType] = (type ?? '').split(';');
        if (payloadType === 'text/plain') {
            signals.onMessage.emit({
                ...meta,
                message,
            });
        } else {
            try {
                signals.onApplicationMessage.emit({
                    ...meta,
                    message: JSON.parse(message) as Record<string, unknown>,
                });
            } catch (error) {
                logger.error(
                    {error},
                    'Could not parse application message payload',
                );
            }
        }
    };

    const handleConferenceStatus: EventHandler<ConferenceStatus> = (
        roomId,
        status,
    ) => {
        conferenceStatus.set(roomId, status);
        if (status.breakout) {
            cache.breakoutRoomNames = undefined;
        }
        signals.onConferenceStatus.emit({id: roomId, status});
    };

    const getSpeakers = (roomId: RoomID, sources?: Sources) => {
        if (!isRoomID(roomId)) {
            return [];
        }
        return (
            sources?.flatMap(source => {
                if (!isParticipantID(source.participant_uuid)) {
                    return [];
                }

                const participant = participants.get(
                    roomId,
                    source.participant_uuid,
                );

                const displayName = participant?.displayName;

                return displayName ? [displayName] : [];
            }) ?? []
        );
    };

    const subscribeSignals = () => [
        eventSignals.onConnected.add(signals.onConnected.emit),
        eventSignals.onPresentationStart.add(event => {
            // With the MCU-side of limitations, we cannot use `presenter_uuid` from
            // the event to reliably correlate the event to the participant.
            // @see {@link https://github.com/pexip/mcu/issues/46379}
            const currentRoom = getCurrentRoomId();
            if (currentRoom === MAIN_ROOM_ID) {
                void currentCall?.receivePresentation(event);
            }
            presentationCache.set(currentRoom, event);
        }),
        eventSignals.onPresentationStop.add(() => {
            const currentRoom = getCurrentRoomId();
            if (currentRoom === MAIN_ROOM_ID) {
                currentCall?.stopReceivingPresentation();
            }
            presentationCache.delete(currentRoom);
        }),
        eventSignals.onCallDisconnected.add(({call_uuid: callUuid}) => {
            logger.debug({callUuid, currentCallUuid}, 'onCallDisconnected');
            currentCall?.disconnect();
            currentCallUuid = undefined;
            signals.onCallDisconnected.emit({call_uuid: callUuid});
        }),
        eventSignals.onBreakoutBegin.add(room => {
            logger.debug({room}, '[Room] Breakout Begin');
            breakoutRooms.set(room.breakout_uuid, room);
            cache.rooms = undefined;
            signals.onBreakoutBegin.emit(room);
        }),
        eventSignals.onBreakoutEvent.add(event => {
            assertRoomID(event.breakout_uuid);
            switch (event.event) {
                case 'participant_sync_begin': {
                    logger.debug(
                        {roomID: event.breakout_uuid},
                        '[Participant] Sync Begin',
                    );
                    participantSync.set(event.breakout_uuid, true);
                    participants.set(event.breakout_uuid);
                    break;
                }
                case 'participant_create': {
                    handleParticipantCreate(
                        event.breakout_uuid,
                        normalizeParticipant(event.data),
                    );
                    break;
                }
                case 'participant_update': {
                    handleParticipantUpdate(
                        event.breakout_uuid,
                        normalizeParticipant(event.data),
                    );
                    break;
                }
                case 'participant_delete': {
                    assertRoomID(event.breakout_uuid);
                    assertParticipantID(event.data.uuid);
                    const participant = participants.get(
                        event.breakout_uuid,
                        event.data.uuid,
                    );
                    if (participant) {
                        handleParticipantDelete(
                            event.breakout_uuid,
                            participant,
                        );
                    }
                    break;
                }
                case 'participant_sync_end': {
                    participantSync.set(event.breakout_uuid, false);
                    handleParticipantsSyncEnded(event.breakout_uuid);
                    break;
                }
                case 'message_received': {
                    handleOnMessage(event.breakout_uuid, event.data);
                    break;
                }
                case 'conference_update': {
                    handleConferenceStatus(
                        event.breakout_uuid,
                        normalizeConferenceState(event.data),
                    );
                    break;
                }

                case 'presentation_start': {
                    if (event.breakout_uuid === currentBreakoutUuid) {
                        void currentCall?.receivePresentation(
                            normalizePresentationEvent(event.data),
                        );
                    }
                    // Cache presentation_start event if presentation is started
                    presentationCache.set(
                        event.breakout_uuid,
                        normalizePresentationEvent(event.data),
                    );
                    break;
                }

                case 'presentation_stop': {
                    if (event.breakout_uuid === currentBreakoutUuid) {
                        currentCall?.stopReceivingPresentation();
                    }
                    // Clear cached presentation_start event if presentation is stopped
                    presentationCache.delete(event.breakout_uuid);
                    break;
                }

                case 'live_captions': {
                    if (event.breakout_uuid === currentBreakoutUuid) {
                        signals.onLiveCaptions.emit({
                            data: event.data.data,
                            isFinal: event.data.is_final,
                            speakers: getSpeakers(
                                event.breakout_uuid,
                                event.data.sources,
                            ),
                        });
                    }
                    break;
                }
            }
        }),
        eventSignals.onBreakoutEnd.add(room => {
            logger.debug({room}, '[Room] Breakout End');
            breakoutRooms.delete(room.breakout_uuid);
            cache.rooms = undefined;
            conferenceStatus.delete(room.breakout_uuid);
            participants.remove(room.breakout_uuid);
            signals.onBreakoutEnd.emit(room);
        }),
        eventSignals.onParticipantCreate.add(participant => {
            handleParticipantCreate(MAIN_ROOM_ID, participant);
        }),
        eventSignals.onParticipantUpdate.add(participant => {
            handleParticipantUpdate(MAIN_ROOM_ID, participant);
        }),
        eventSignals.onParticipantDelete.add(uuid => {
            if (isParticipantID(uuid)) {
                const leftParticipant = participants.get(MAIN_ROOM_ID, uuid);
                if (leftParticipant) {
                    handleParticipantDelete(MAIN_ROOM_ID, leftParticipant);
                }
            }
        }),
        eventSignals.onParticipantSyncBegin.add(() => {
            logger.debug({roomID: MAIN_ROOM_ID}, '[Participant] Sync Begin');
            participantSync.set(MAIN_ROOM_ID, true);
            participants.set(MAIN_ROOM_ID);
        }),
        eventSignals.onParticipantSyncEnd.add(() => {
            participantSync.set(MAIN_ROOM_ID, false);
            handleParticipantsSyncEnded(MAIN_ROOM_ID);
        }),
        eventSignals.onMessage.add(event => {
            handleOnMessage(MAIN_ROOM_ID, event);
        }),
        eventSignals.onLayoutUpdate.add(event => {
            signals.onRequestedLayout.emit({
                primaryScreen: {
                    hostLayout:
                        event.requested_layout?.primary_screen.chair_layout,
                    guestLayout:
                        event.requested_layout?.primary_screen.guest_layout,
                },
            });
            signals.onLayoutOverlayTextEnabled.emit(
                !!event.overlay_text_enabled,
            );
            signals.onLayoutUpdate.emit(event);
        }),
        eventSignals.onStageUpdate.add(stages =>
            signals.onStage.emit(
                stages.map(stage => ({
                    userId: stage.participant_uuid,
                    stageIndex: stage.stage_index,
                    vad: stage.vad,
                })),
            ),
        ),
        eventSignals.onDisconnect.add(({reason}) => {
            cleanupAndDisconnect(reason);
        }),
        eventSignals.onConferenceUpdate.add(status => {
            handleConferenceStatus(MAIN_ROOM_ID, status);
        }),
        eventSignals.onRefer.add(details => {
            logger.debug({details}, '[Room] Refer');
            signals.onTransfer.emit({
                ...details,
                callTag: currentCallTag,
                breakoutName: details.breakout_name,
                target: details.target,
            });
        }),
        eventSignals.onCancelRefer.add(details => {
            logger.debug({details}, '[Room] Cancel Refer');
            signals.onCancelTransfer.emit(details);
        }),
        eventSignals.onLiveCaptions.add(captions => {
            signals.onLiveCaptions.emit({
                data: captions.data,
                isFinal: captions.is_final,
                speakers: getSpeakers(MAIN_ROOM_ID, captions.sources),
            });
        }),
        eventSignals.onSplashScreen.add(result => {
            if (!result || !('screen_key' in result)) {
                signals.onSplashScreen.emit();
                return;
            }
            const splashScreen = themeSchema?.[result.screen_key];
            if (splashScreen) {
                signals.onSplashScreen.emit({
                    screenKey: result.screen_key,
                    text: splashScreen.elements[0]?.text ?? '',
                    background: getBackgroundUrl(splashScreen.background.path),
                    displayDuration: result.display_duration * 1000,
                });
            }
        }),
        eventSignals.onNewOffer.add(({sdp}) => {
            signals.onNewOffer.emit(sdp);
        }),
        eventSignals.onUpdateSdp.add(({sdp}) => {
            signals.onUpdateSdp.emit(sdp);
        }),
        eventSignals.onNewCandidate.add(
            ({candidate, mid: sdpMid, ufrag: usernameFragment}) => {
                signals.onIceCandidate.emit(
                    new RTCIceCandidate({candidate, sdpMid, usernameFragment}),
                );
            },
        ),
        eventSignals.onPeerDisconnect.add(() => {
            callSignals.onSecureCheckCode.emit('');
            signals.onPeerDisconnect.emit();
        }),
        eventSignals.onRefreshToken.add(() => {
            void requestClient?.refreshToken();
        }),
        eventSignals.onBreakoutRefer.add(
            ({breakout_uuid, breakout_name, requester_uuid, ...rest}) => {
                logger.debug(
                    {
                        details: {
                            ...rest,
                            breakoutUuid: breakout_uuid,
                            breakoutName: breakout_name,
                            requesterUuid: requester_uuid,
                        },
                    },
                    '[Room] Breakout Refer',
                );
                assertRoomID(breakout_uuid);
                signals.onBreakoutRefer.emit({
                    breakoutUuid: breakout_uuid,
                    breakoutName: breakout_name,
                    requesterUuid: requester_uuid,
                });
            },
        ),
        eventSignals.onFecc.add(feccEvent => {
            signals.onFecc.emit(feccEvent);
        }),
        callSignals.onSecureCheckCode.add(code => {
            secureCheckCode = code;
        }),
        callSignals.onRtcStats.add(stats => {
            latestStats = {
                inbound: {
                    audio: stats.inbound.audio ?? latestStats?.inbound.audio,
                    video: stats.inbound.video ?? latestStats?.inbound.video,
                    preso: stats.inbound.preso ?? latestStats?.inbound.preso,
                },
                outbound: {
                    audio: stats.outbound.audio ?? latestStats?.outbound.audio,
                    video: stats.outbound.video ?? latestStats?.outbound.video,
                    preso: stats.outbound.preso ?? latestStats?.outbound.preso,
                },
            };
        }),
        callSignals.onCallConnected.add(signals.onCallConnected.emit),
    ];

    const present = (stream?: MediaStream) => {
        void currentCall?.present(stream);
    };

    const stopPresenting = () => {
        void currentCall?.stopPresenting();
    };

    const setStream = (stream: MediaStream) => {
        currentCall?.setStream(stream);
    };

    const setBandwidth = (bandwidth: number) => {
        currentCall?.setBandwidth({
            in: getBandwidth(bandwidth, bandwidthIn),
            out: getBandwidth(bandwidth, bandwidthOut),
        });
    };
    const setDegradationPreference = (preference: DegradationPreference) => {
        currentCall?.setDegradationPreference(preference);
    };

    const setLayout = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'setLayout'>, fetcher) => {
            const {
                transforms,
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                breakoutUuid = currentBreakoutUuid,
            } = reqParams[0];

            if (breakoutUuid) {
                return API.breakoutTransformLayout({
                    fetcher,
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                    },
                    host,
                    body: {
                        transforms,
                    },
                });
            }
            return API.transformLayout({
                fetcher,
                params: {
                    conferenceAlias,
                },
                host,
                body: {
                    transforms,
                },
            });
        },
        funcName: 'setLayout',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const setPersonalLayout = generateEndpoint({
        func: async (
            reqParams: GetEndpointParams<'setPersonalLayout'>,
            fetcher,
        ) => {
            const {
                transforms,
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                breakoutUuid = currentBreakoutUuid,
                mixName = DEFAULT_PERSONAL_MIX,
            } = reqParams[0];

            const currentMixName = getMe()?.receiveFromVideoMix?.mix_name;

            if (breakoutUuid) {
                if (currentMixName === DEFAULT_MIX) {
                    await API.breakoutVideoMixesCreate({
                        fetcher,
                        params: {
                            conferenceAlias,
                            breakoutUuid,
                        },
                        host,
                        body: {
                            mix_name: mixName,
                        },
                    });
                }

                return API.breakoutVideoMixesConfigure({
                    fetcher,
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                        mixName,
                    },
                    host,
                    body: {
                        transform_layout: transforms,
                    },
                });
            }

            if (currentMixName === DEFAULT_MIX) {
                await API.videoMixesCreate({
                    fetcher,
                    params: {
                        conferenceAlias,
                    },
                    host,
                    body: {
                        mix_name: mixName,
                    },
                });
            }

            return API.videoMixesConfigure({
                fetcher,
                params: {
                    conferenceAlias,
                    mixName,
                },
                host,
                body: {
                    transform_layout: transforms,
                },
            });
        },
        funcName: 'setPersonalLayout',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const deleteVideoMix = generateEndpoint({
        func: async (
            reqParams: GetEndpointParams<'deleteVideoMix'>,
            fetcher,
        ) => {
            const {
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                breakoutUuid = currentBreakoutUuid,
                mixName = getMe()?.receiveFromVideoMix?.mix_name,
            } = reqParams[0];

            if (!mixName) {
                throw new Error('Mix name is required');
            }

            if (breakoutUuid) {
                return API.breakoutVideoMixesDelete({
                    fetcher,
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                        mixName,
                    },
                    host,
                });
            }

            return API.videoMixesDelete({
                fetcher,
                params: {
                    conferenceAlias,
                    mixName,
                },
                host,
            });
        },
        funcName: 'deleteVideoMix',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const raiseHand = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'raiseHand'>, fetcher) => {
            const {
                raise,
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                participantUuid = getMe()?.uuid ?? currentParticipantUuid,
                breakoutUuid = currentBreakoutUuid,
            } = reqParams[0];

            if (breakoutUuid) {
                return API[
                    raise
                        ? 'breakoutBuzzParticipant'
                        : 'breakoutClearbuzzParticipant'
                ]({
                    fetcher,
                    params: {
                        conferenceAlias,
                        participantUuid,
                        breakoutUuid,
                    },
                    host,
                });
            }
            return API[raise ? 'buzzParticipant' : 'clearbuzzParticipant']({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                },
                host,
            });
        },
        funcName: 'raiseHand',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const lowerAllRaisedHands = generateEndpoint({
        func: async (
            reqParams: GetEndpointParams<'lowerAllRaisedHands'>,
            fetcher,
        ) => {
            const {
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                breakoutUuid = currentBreakoutUuid,
            } = reqParams[0];

            if (breakoutUuid) {
                return API.breakoutClearAllBuzz({
                    fetcher,
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                    },
                    host,
                });
            }
            return API.clearAllBuzz({
                fetcher,
                params: {
                    conferenceAlias,
                },
                host,
            });
        },
        funcName: 'lowerAllRaisedHands',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const spotlight = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'spotlight'>, fetcher) => {
            const {
                enable,
                participantUuid,
                conferenceAlias = currentConferenceAlias,
                breakoutUuid = currentBreakoutUuid,
                host = currentHost,
            } = reqParams[0];

            if (breakoutUuid) {
                return API[
                    enable
                        ? 'breakoutSpotlightonParticipant'
                        : 'breakoutSpotlightoffParticipant'
                ]({
                    fetcher,
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                        participantUuid,
                    },
                    host,
                });
            }

            return API[
                enable ? 'spotlightonParticipant' : 'spotlightoffParticipant'
            ]({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                },
                host,
            });
        },
        funcName: 'spotlight',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const sendMessageRequest = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'sendMessage'>, fetcher) => {
            const {
                payload,
                type,
                participantUuid,
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                breakoutUuid = currentBreakoutUuid,
            } = reqParams[0];

            if (currentCall && conferenceFeatureFlags?.isDirectMedia) {
                // Use Logical OR: Fallback display name if the participant don't have a display name explicitly set
                const origin =
                    participants.get(MAIN_ROOM_ID, currentParticipantUuid)
                        ?.displayName || 'User'; // empty string should be replaced
                currentCall.sendDataChannelEvent({
                    type: 'message',
                    body: {
                        type,
                        origin,
                        uuid: currentParticipantUuid,
                        payload,
                    },
                });
                return Promise.resolve({
                    status: 200,
                    data: {status: 'success', result: true},
                } as GetEndpointResponse<'sendMessage'>);
            }

            const common = {
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                },
                host,
                body: {
                    payload,
                    type,
                },
            };
            if (participantUuid) {
                if (breakoutUuid) {
                    return API.breakoutMessageParticipant({
                        ...common,
                        params: {
                            conferenceAlias,
                            breakoutUuid,
                            participantUuid,
                        },
                    });
                }
                return API.messageParticipant({
                    ...common,
                    params: {
                        conferenceAlias,
                        participantUuid,
                    },
                });
            }
            if (breakoutUuid) {
                return API.breakoutMessageConference({
                    ...common,
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                    },
                });
            }
            return API.messageConference({
                ...common,
                params: {
                    conferenceAlias,
                },
            });
        },
        funcName: 'sendMessage',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
        retriable: true,
    });

    const sendMessage = async (
        params: Parameters<InfinityClient['sendMessage']>[0],
    ) => {
        return sendMessageRequest({...params, type: 'text/plain'});
    };

    const sendApplicationMessage = async (
        params: Parameters<InfinityClient['sendApplicationMessage']>[0],
    ) => {
        try {
            const payload = JSON.stringify(params.payload);
            return sendMessageRequest({
                ...params,
                payload,
                type: 'application/json',
            });
        } catch (error) {
            logger.error({error}, 'Could not stringify application message');
        }
    };

    const admit = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'admit'>, fetcher) => {
            const {
                participantUuid,
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                breakoutUuid = currentBreakoutUuid,
            } = reqParams[0];

            if (breakoutUuid) {
                return API.breakoutUnlockParticipant({
                    fetcher,
                    params: {
                        conferenceAlias,
                        participantUuid,
                        breakoutUuid,
                    },
                    host,
                });
            }

            return API.unlockParticipant({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                },
                host,
            });
        },
        funcName: 'admit',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const mute = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'mute'>, fetcher) => {
            const {
                mute,
                participantUuid = getMe()?.uuid ?? currentParticipantUuid,
                conferenceAlias = currentConferenceAlias,
                breakoutUuid = currentBreakoutUuid,
                host = currentHost,
            } = reqParams[0];

            if (breakoutUuid) {
                return API[
                    mute
                        ? 'breakoutMuteParticipant'
                        : 'breakoutUnmuteParticipant'
                ]({
                    fetcher,
                    params: {
                        conferenceAlias,
                        participantUuid,
                        breakoutUuid,
                    },
                    host,
                });
            }

            return API[mute ? 'muteParticipant' : 'unmuteParticipant']({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                },
                host,
            });
        },
        funcName: 'mute',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const clientMute = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'mute'>, fetcher) => {
            const {
                mute,
                participantUuid = getMe()?.uuid ?? currentParticipantUuid,
                conferenceAlias = currentConferenceAlias,
                breakoutUuid = currentBreakoutUuid,
                host = currentHost,
            } = reqParams[0];

            if (breakoutUuid) {
                return API[
                    mute
                        ? 'breakoutClientMuteParticipant'
                        : 'breakoutClientUnmuteParticipant'
                ]({
                    fetcher,
                    params: {
                        conferenceAlias,
                        participantUuid,
                        breakoutUuid,
                    },
                    host,
                });
            }

            return API[
                mute ? 'clientMuteParticipant' : 'clientUnmuteParticipant'
            ]({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                },
                host,
            });
        },
        funcName: 'mute',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const muteAllGuests = generateEndpoint({
        func: async (
            reqParams: GetEndpointParams<'muteAllGuests'>,
            fetcher,
        ) => {
            const {
                mute,
                conferenceAlias = currentConferenceAlias,
                breakoutUuid = currentBreakoutUuid,
                host = currentHost,
            } = reqParams[0];

            if (breakoutUuid) {
                return API[
                    mute ? 'breakoutMuteguests' : 'breakoutUnmuteguests'
                ]({
                    fetcher,
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                    },
                    host,
                });
            }

            return API[mute ? 'muteguests' : 'unmuteguests']({
                fetcher,
                params: {
                    conferenceAlias,
                },
                host,
            });
        },
        funcName: 'muteAllGuests',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const guestsCanUnmute = generateEndpoint({
        func: async (
            reqParams: GetEndpointParams<'guestsCanUnmute'>,
            fetcher,
        ) => {
            const {
                setting,
                conferenceAlias = currentConferenceAlias,
                breakoutUuid = currentBreakoutUuid,
                host = currentHost,
            } = reqParams[0];

            if (breakoutUuid) {
                return API.breakoutSetGuestsCanUnmute({
                    fetcher,
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                    },
                    host,
                    body: {
                        setting,
                    },
                });
            }

            return API.setGuestsCanUnmute({
                fetcher,
                params: {
                    conferenceAlias,
                },
                host,
                body: {
                    setting,
                },
            });
        },
        funcName: 'guestsCanUnmute',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const muteVideo = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'muteVideo'>, fetcher) => {
            const {
                muteVideo,
                participantUuid = getMe()?.uuid ?? currentParticipantUuid,
                conferenceAlias = currentConferenceAlias,
                breakoutUuid = currentBreakoutUuid,
                host = currentHost,
            } = reqParams[0];

            if (breakoutUuid) {
                return API[
                    muteVideo
                        ? 'breakoutVideoMuteParticipant'
                        : 'breakoutVideoUnmuteParticipant'
                ]({
                    fetcher,
                    params: {
                        breakoutUuid,
                        conferenceAlias,
                        participantUuid,
                    },
                    host,
                });
            }

            return API[
                muteVideo ? 'videoMuteParticipant' : 'videoUnmuteParticipant'
            ]({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                },
                host,
            });
        },
        funcName: 'muteVideo',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const lock = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'lock'>, fetcher) => {
            const {
                lock,
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                breakoutUuid = currentBreakoutUuid,
            } = reqParams[0];

            if (breakoutUuid) {
                return API[lock ? 'breakoutLock' : 'breakoutUnlock']({
                    fetcher,
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                    },
                    host,
                });
            }
            return API[lock ? 'lock' : 'unlock']({
                fetcher,
                params: {
                    conferenceAlias,
                },
                host,
            });
        },
        funcName: 'lock',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const disconnectAll = generateEndpoint({
        func: async (
            reqParams: GetEndpointParams<'disconnectAll'>,
            fetcher,
        ) => {
            const {
                conferenceAlias = currentConferenceAlias,
                breakoutUuid = currentBreakoutUuid,
                host = currentHost,
            } = reqParams[0];

            if (breakoutUuid) {
                return API.breakoutDisconnect({
                    fetcher,
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                    },
                    host,
                });
            }

            return API.disconnect({
                fetcher,
                params: {
                    conferenceAlias,
                },
                host,
            });
        },
        funcName: 'disconnectAll',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const kick = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'kick'>, fetcher) => {
            const {
                participantUuid,
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                breakoutUuid = currentBreakoutUuid,
            } = reqParams[0];

            if (breakoutUuid) {
                return API.breakoutDisconnectParticipant({
                    fetcher,
                    params: {
                        conferenceAlias,
                        participantUuid,
                        breakoutUuid,
                    },
                    host,
                });
            }

            return API.disconnectParticipant({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                },
                host,
            });
        },
        funcName: 'kick',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const dial = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'dial'>, fetcher) => {
            const {
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                protocol = 'auto',
                ...body
            } = reqParams[0];

            return API.dial({
                fetcher,
                params: {
                    conferenceAlias,
                },
                body: {
                    protocol,
                    ...body,
                },
                host,
            });
        },
        funcName: 'dial',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const transfer = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'transfer'>, fetcher) => {
            const {
                destination,
                pin,
                role,
                participantUuid,
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                breakoutUuid = currentBreakoutUuid,
            } = reqParams[0];

            if (breakoutUuid) {
                return API.breakoutTransferParticipant({
                    fetcher,
                    body: {
                        conference_alias: destination,
                        pin,
                        role,
                    },
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                        participantUuid,
                    },
                    host,
                });
            }

            return API.transferParticipant({
                fetcher,
                body: {
                    conference_alias: destination,
                    pin,
                    role,
                },
                params: {
                    conferenceAlias,
                    participantUuid,
                },
                host,
            });
        },
        funcName: 'transfer',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const liveCaptions = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'liveCaptions'>, fetcher) => {
            const {
                enable,
                conferenceAlias = currentConferenceAlias,
                participantUuid = currentParticipantUuid,
                breakoutUuid = currentBreakoutUuid,
            } = reqParams[0];

            const breakoutParticipantUuid =
                getBreakoutParticipantUuid(breakoutUuid);

            return breakoutUuid && breakoutParticipantUuid
                ? API[
                      enable
                          ? 'breakoutShowLiveCaptions'
                          : 'breakoutHideLiveCaptions'
                  ]({
                      fetcher,
                      params: {
                          conferenceAlias,
                          participantUuid: breakoutParticipantUuid,
                          breakoutUuid,
                      },
                      host: currentHost,
                  })
                : API[enable ? 'showLiveCaptions' : 'hideLiveCaptions']({
                      fetcher,
                      params: {
                          conferenceAlias,
                          participantUuid,
                      },
                      host: currentHost,
                  });
        },
        funcName: 'liveCaptions',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const setRole = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'setRole'>, fetcher) => {
            const {
                role,
                participantUuid,
                conferenceAlias = currentConferenceAlias,
            } = reqParams[0];

            await API.roleParticipant({
                fetcher,
                body: {
                    role,
                },
                params: {
                    conferenceAlias,
                    participantUuid,
                },
                host: currentHost,
            });
        },
        funcName: 'setRole',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const setPin = (pin = NONE) => {
        currentPin = pin;
    };

    const setSsoToken = (ssoToken = NONE) => {
        currentSsoToken = ssoToken;
    };

    const setToken = (token: string) => {
        currentToken = token;
    };

    const setConferenceExtension = (conferenceExtension?: string) => {
        currentConferenceExtension = conferenceExtension;
    };

    const setCallTag = (callTag?: string) => {
        currentCallTag = callTag;
    };

    const statistics = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'statistics'>, fetcher) => {
            const {
                audio,
                video,
                presentation,
                conferenceAlias = currentConferenceAlias,
                participantUuid = currentParticipantUuid,
                callUuid = currentCallUuid,
                host = currentHost,
            } = reqParams[0];

            if (!callUuid) {
                return;
            }

            await API.statistics({
                fetcher,
                body: {
                    audio,
                    video,
                    presentation,
                },
                params: {
                    conferenceAlias,
                    participantUuid,
                    callUuid,
                },
                host,
            });
        },
        funcName: 'statistics',
        fetcher: fetchWithDebugLog,
        requiresToken: true,
    });

    const requestTheme = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'requestTheme'>, fetcher) => {
            const {
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
            } = reqParams[0];

            try {
                const response = await API.theme({
                    fetcher,
                    params: {
                        conferenceAlias,
                    },
                    host,
                });

                if (response.status !== 200) {
                    return;
                }

                return response.data.result;
            } catch (error) {
                logger.error({error}, `Can't request theme.`);
            }
        },
        funcName: 'requestTheme',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const setTextOverlay = generateEndpoint({
        func: async (
            reqParams: GetEndpointParams<'setTextOverlay'>,
            fetcher,
        ) => {
            const {
                text,
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                participantUuid,
            } = reqParams[0];

            return API.overlaytextParticipant({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                },
                body: {
                    text,
                },
                host,
            });
        },
        funcName: 'setTextOverlay',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const sendDTMF = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'sendDTMF'>, fetcher) => {
            const {
                digits,
                participantUuid,
                callUuid,
                conferenceAlias = currentConferenceAlias,
                breakoutUuid = currentBreakoutUuid,
                host = currentHost,
            } = reqParams[0];

            const common = {
                fetcher,
                body: {
                    digits,
                },
                host,
            };

            if (!participantUuid) {
                if (!currentCallUuid) {
                    throw new Error('Call uuid is not present');
                }
                return API.dtmf({
                    ...common,
                    params: {
                        conferenceAlias,
                        participantUuid: currentParticipantUuid,
                        callUuid: currentCallUuid,
                    },
                });
            }

            if (callUuid) {
                return API.dtmf({
                    ...common,
                    params: {
                        conferenceAlias,
                        participantUuid,
                        callUuid,
                    },
                });
            }

            if (breakoutUuid) {
                return API.breakoutDtmfParticipant({
                    ...common,
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                        participantUuid,
                    },
                });
            }

            return API.dtmfParticipant({
                ...common,
                params: {
                    conferenceAlias,
                    participantUuid,
                },
            });
        },
        funcName: 'sendDTMF',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const requestAspectRatio = (
        reqParams: Parameters<Client['requestAspectRatio']>[0],
    ) => {
        if (conferenceFeatureFlags?.isDirectMedia) {
            return Promise.resolve(undefined);
        }

        const fetcher = requestClient?.fetcher;

        if (!fetcher) {
            return Promise.reject(
                new Error('No fetcher defined to call requestAspectRatio'),
            );
        }

        const {
            aspectRatio,
            conferenceAlias = currentConferenceAlias,
            host = currentHost,
            participantUuid = currentParticipantUuid,
            abortSignal,
        } = reqParams;

        return API.preferredAspectRatio({
            fetcher,
            params: {
                conferenceAlias,
                participantUuid,
            },
            body: {
                aspect_ratio: aspectRatio,
            },
            host,
            init: {signal: abortSignal},
        });
    };

    const breakout = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'breakout'>, fetcher) => {
            const res = await API.breakouts({
                fetcher,
                params: {
                    conferenceAlias: currentConferenceAlias,
                },
                body: reqParams[0],
                host: currentHost,
            });
            const {result} = res.data;
            assertRoomID(result.breakout_uuid);
            return {breakout_uuid: result.breakout_uuid};
        },
        funcName: 'breakout',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const breakoutMoveParticipants = generateEndpoint({
        func: async (
            reqParams: GetEndpointParams<'breakoutMoveParticipants'>,
            fetcher,
        ) => {
            const {fromBreakoutUuid, toRoomUuid, participants} = reqParams[0];

            fromBreakoutUuid
                ? await API.breakoutMoveParticipants({
                      fetcher,
                      params: {
                          conferenceAlias: currentConferenceAlias,
                          breakoutUuid: fromBreakoutUuid,
                      },
                      body: {
                          breakout_uuid: toRoomUuid,
                          participants: participants,
                      },
                      host: currentHost,
                  })
                : await API.breakoutMoveParticipantsFromMain({
                      fetcher,
                      params: {
                          conferenceAlias: currentConferenceAlias,
                      },
                      body: {
                          breakout_uuid: toRoomUuid,
                          participants: participants,
                      },
                      host: currentHost,
                  });
            return;
        },
        funcName: 'breakoutMoveParticipants',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const closeBreakouts = generateEndpoint({
        func: async (
            _reqParams: GetEndpointParams<'closeBreakouts'>,
            fetcher,
        ) => {
            return API.breakoutsDisconnect({
                fetcher,
                params: {
                    conferenceAlias: currentConferenceAlias,
                },
                host: currentHost,
            });
        },
        funcName: 'closeBreakouts',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const closeBreakoutRoom = generateEndpoint({
        func: async (
            reqParams: GetEndpointParams<'closeBreakoutRoom'>,
            fetcher,
        ) => {
            return API.breakoutDisconnect({
                fetcher,
                params: {
                    conferenceAlias: currentConferenceAlias,
                    breakoutUuid: reqParams[0].breakoutUuid,
                },
                host: currentHost,
            });
        },
        funcName: 'closeBreakoutRoom',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const emptyBreakouts = generateEndpoint({
        func: async (
            _reqParams: GetEndpointParams<'emptyBreakouts'>,
            fetcher,
        ) => {
            return API.breakoutsEmpty({
                fetcher,
                params: {
                    conferenceAlias: currentConferenceAlias,
                },
                host: currentHost,
            });
        },
        funcName: 'emptyBreakouts',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    // TODO: use generateEndpoint?
    const joinBreakoutRoom = async ({
        breakoutUuid,
        bandwidth: bandwidthOpt,
        mediaStream: mediaStreamOpt,
        ...opt
    }: {
        breakoutUuid?: string;
        bandwidth?: number;
        mediaStream?: MediaStream | (() => MediaStream | undefined);
    }) => {
        const bandwidth = ((bandwidthOpt !== undefined && {
            in: getBandwidth(bandwidthOpt, bandwidthIn),
            out: getBandwidth(bandwidthOpt, bandwidthOut),
        }) ||
            (currentCall?.bandwidth as Bandwidth)) ?? {
            in: 0,
            out: 0,
        };
        const mediaStream = mediaStreamOpt ?? currentCall?.mediaStream;
        await disconnectRequest({
            /**
             * There is a specific case for direct media call. When
             * one peer disconnects we need to restart webrtc call
             * and wait for another participant to arrive
             * so we need to keep EventSource open to wait for the
             * ping from mcu for that.
             */
            callback: async () => {
                if (
                    currentCall?.presoState.send === 'connected' ||
                    currentCall?.presoState.send === 'connecting'
                ) {
                    await currentCall?.stopPresenting();
                }
                currentCall?.disconnect();
                currentCall = undefined;
                currentCallUuid = undefined;
            },
            // Reuse existing token for this use case
            release: () => {
                return Promise.resolve();
            },
        });
        if (isRoomID(breakoutUuid) && breakoutUuid !== MAIN_ROOM_ID) {
            currentBreakoutUuid = breakoutUuid;
        } else {
            currentBreakoutUuid = undefined;
        }
        startCall({
            ...opt,
            bandwidth,
            mediaStream,
        });
        // Propagate presentation event if room has a running presentation
        // We assume that we join main room if currentBreakoutUuid is undefined
        const presentationEvent = presentationCache.get(getCurrentRoomId());
        if (presentationEvent) {
            await currentCall?.receivePresentation(presentationEvent);
        }
    };

    const guestLeaveBreakoutRoom = generateEndpoint({
        func: async (
            _reqParams: GetEndpointParams<'guestLeaveBreakoutRoom'>,
            fetcher,
        ) => {
            return API.guestLeaveBreakout({
                fetcher,
                params: {
                    conferenceAlias: currentConferenceAlias,
                },
                host: currentHost,
            });
        },
        funcName: 'guestLeaveBreakoutRoom',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const sendConferenceRequest = generateEndpoint({
        func: async (
            reqParams: GetEndpointParams<'sendConferenceRequest'>,
            fetcher,
        ) => {
            const {path, method, payload} = reqParams[0];
            const uri = `${currentHost}/api/client/v2/conferences/${currentConferenceAlias}/${path}`;
            try {
                const body = payload ? JSON.stringify(payload) : undefined;
                const res = await fetcher(uri, {
                    method,
                    body,
                    headers: {
                        'Content-Type': APPLICATION_JSON,
                    },
                });

                const responseContentType = res.headers.get('Content-Type');
                if (responseContentType !== APPLICATION_JSON) {
                    logger.error(
                        {responseContentType, APPLICATION_JSON},
                        `Response Content-Type is not ${APPLICATION_JSON}`,
                    );
                    return {status: res.status, data: undefined};
                }
                try {
                    const data: unknown = await res.json();
                    return {status: res.status, data};
                } catch (error) {
                    logger.error({error}, 'Could not get response data: ');
                }
            } catch (error) {
                logger.error({error}, 'Could not stringify payload');
            }
        },
        funcName: 'sendConferenceRequest',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const requestParticipants = generateEndpoint({
        func: async (
            reqParams: GetEndpointParams<'requestParticipants'>,
            fetcher,
        ) => {
            const {
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
            } = reqParams[0];

            try {
                return await API.participants({
                    fetcher,
                    params: {
                        conferenceAlias,
                    },
                    host,
                });
            } catch (error) {
                logger.error({error}, `Can't request participants.`);
            }
        },
        funcName: 'requestParticipants',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const startConference = generateEndpoint({
        func: async (
            reqParams: GetEndpointParams<'startConference'>,
            fetcher,
        ) => {
            const {
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
            } = reqParams[0];

            return API.startConference({
                fetcher,
                params: {
                    conferenceAlias,
                },
                host,
            });
        },
        funcName: 'startConference',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const fecc = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'fecc'>, fetcher) => {
            const {target, action, movement, timeout} = reqParams[0];

            if (target === currentParticipantUuid) {
                signals.onFecc.emit({action, movement, timeout});
                return Promise.resolve({
                    status: 200,
                    data: {status: 'success', result: true},
                } as GetEndpointResponse<'fecc'>);
            }

            if (currentCall && conferenceFeatureFlags?.isDirectMedia) {
                currentCall.sendDataChannelEvent({
                    type: 'fecc',
                    body: {
                        action,
                        movement,
                        timeout,
                    },
                });
                return Promise.resolve({
                    status: 200,
                    data: {status: 'success', result: true},
                } as GetEndpointResponse<'fecc'>);
            }

            if (!target) {
                if (!currentCallUuid) {
                    throw new Error('Call uuid is not present');
                }
                return API.fecc({
                    fetcher,
                    params: {
                        conferenceAlias: currentConferenceAlias,
                        participantUuid: currentParticipantUuid,
                        callUuid: currentCallUuid,
                    },
                    body: {
                        action,
                        movement,
                        timeout,
                    },
                    host: currentHost,
                });
            }

            if (currentBreakoutUuid) {
                return API.breakoutFeccParticipant({
                    fetcher,
                    params: {
                        conferenceAlias: currentConferenceAlias,
                        breakoutUuid: currentBreakoutUuid,
                        participantUuid: target,
                    },
                    body: {
                        action,
                        target,
                        movement,
                        timeout,
                    },
                    host: currentHost,
                });
            }

            return API.feccParticipant({
                fetcher,
                params: {
                    conferenceAlias: currentConferenceAlias,
                    participantUuid: target,
                },
                body: {
                    action,
                    target,
                    movement,
                    timeout,
                },
                host: currentHost,
            });
        },
        funcName: 'fecc',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const breakoutAskForHelp = generateEndpoint({
        func: async (
            reqParams: GetEndpointParams<'breakoutAskForHelp'>,
            fetcher,
        ) => {
            const {
                conferenceAlias = currentConferenceAlias,
                breakoutUuid = currentBreakoutUuid,
                host = currentHost,
            } = reqParams[0];

            if (breakoutUuid && breakoutUuid !== MAIN_ROOM_ID) {
                return API.hostBreakoutBuzz({
                    fetcher,
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                    },
                    host,
                });
            }

            return API.guestBreakoutBuzz({
                fetcher,
                params: {
                    conferenceAlias,
                },
                host,
            });
        },
        funcName: 'breakoutAskForHelp',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const breakoutRemoveAskForHelp = generateEndpoint({
        func: async (
            reqParams: GetEndpointParams<'breakoutRemoveAskForHelp'>,
            fetcher,
        ) => {
            const {
                conferenceAlias = currentConferenceAlias,
                breakoutUuid = currentBreakoutUuid,
                host = currentHost,
            } = reqParams[0];

            if (breakoutUuid && breakoutUuid !== MAIN_ROOM_ID) {
                return API.hostClearBreakoutBuzz({
                    fetcher,
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                    },
                    host,
                });
            }

            return API.guestClearBreakoutBuzz({
                fetcher,
                params: {
                    conferenceAlias,
                },
                host,
            });
        },
        funcName: 'breakoutRemoveAskForHelp',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const availableLayouts = generateEndpoint({
        func: async (
            reqParams: GetEndpointParams<'availableLayouts'>,
            fetcher,
        ) => {
            const {
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
                annotate = false,
            } = reqParams[0];

            const res = await API.availableLayouts({
                fetcher,
                params: {
                    conferenceAlias,
                    annotate,
                },
                host,
            });

            return res.data.result;
        },
        funcName: 'availableLayouts',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const layoutSvgs = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'layoutSvgs'>, fetcher) => {
            const {
                conferenceAlias = currentConferenceAlias,
                host = currentHost,
            } = reqParams[0];

            const res = await API.layoutSvgs({
                fetcher,
                params: {
                    conferenceAlias,
                },
                host,
            });

            return res.data.result;
        },
        funcName: 'layoutSvgs',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const presInMix = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'presInMix'>, fetcher) => {
            const {
                state,
                conferenceAlias = currentConferenceAlias,
                participantUuid = currentParticipantUuid,
                host = currentHost,
            } = reqParams[0];

            const res = await API.presInMixParticipant({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                },
                body: {
                    state,
                },
                host,
            });

            return res;
        },
        funcName: 'presInMix',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const notifyNotAFK = generateEndpoint({
        func: async (reqParams: GetEndpointParams<'notifyNotAFK'>, fetcher) => {
            const {
                conferenceAlias = currentConferenceAlias,
                participantUuid = currentParticipantUuid,
                host = currentHost,
            } = reqParams[0];

            const res = await API.notifyNotAFK({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                },
                host,
            });

            return res;
        },
        funcName: 'notifyNotAFK',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const getBackgroundUrl = (path: string) =>
        `${currentHost}/api/client/v2/conferences/${currentConferenceAlias}/theme/${path}?token=${requestClient?.token}`;

    const getMe = (roomId?: string) => {
        if (roomId === MAIN_ROOM_ID) {
            return participants.get(roomId, currentParticipantUuid);
        }
        const breakoutParticipantUuid = getBreakoutParticipantUuid(
            roomId ?? currentBreakoutUuid,
        );
        return participants.get(
            currentBreakoutUuid || MAIN_ROOM_ID,
            breakoutParticipantUuid
                ? breakoutParticipantUuid
                : currentParticipantUuid,
        );
    };

    return {
        get localCallType() {
            return currentCallType;
        },
        get roomId() {
            return currentBreakoutUuid ?? MAIN_ROOM_ID;
        },
        get breakoutRooms() {
            return breakoutRooms;
        },
        get breakoutRoomNames() {
            if (cache.breakoutRoomNames) {
                return cache.breakoutRoomNames;
            }
            const names = new Map<RoomID, string>();
            for (const roomId of breakoutRooms.keys()) {
                const status = conferenceStatus.get(roomId);
                if (status?.breakoutName) {
                    names.set(roomId, status.breakoutName);
                }
            }
            cache.breakoutRoomNames = names;
            return names;
        },
        get rooms() {
            if (cache.rooms) {
                return cache.rooms;
            }
            const rooms = [MAIN_ROOM_ID];
            for (const room of breakoutRooms.keys()) {
                rooms.push(room);
            }
            cache.rooms = rooms;
            return rooms;
        },
        get conferenceName() {
            return currentConferenceName;
        },
        get conferenceStatus() {
            return conferenceStatus;
        },
        get conferenceFeatureFlags() {
            return conferenceFeatureFlags;
        },
        get secureCheckCode() {
            return secureCheckCode;
        },
        get latestStats() {
            return latestStats;
        },
        get serviceType() {
            return currentServiceType;
        },
        get isFeccEnabled() {
            return gatewayParticipantCanFecc();
        },
        getParticipants(roomID) {
            return participants.getAll(roomID);
        },
        setFeccSupported(canFecc) {
            feccSupported = canFecc;
        },
        isBreakoutRoom(roomId) {
            return Boolean(conferenceStatus.get(roomId)?.breakout);
        },
        getMe,
        admit,
        call,
        deleteVideoMix,
        dial,
        disconnect,
        disconnectAll,
        kick,
        liveCaptions,
        lock,
        mute,
        clientMute,
        guestsCanUnmute,
        muteAllGuests,
        muteVideo,
        present,
        raiseHand,
        lowerAllRaisedHands,
        restartCall,
        sendMessage,
        sendApplicationMessage,
        setBandwidth,
        setDegradationPreference,
        setConferenceExtension,
        setCallTag,
        setLayout,
        setPersonalLayout,
        setPin,
        setSsoToken,
        setToken,
        setRole,
        setStream,
        spotlight,
        stopPresenting,
        transfer,
        setTextOverlay,
        sendDTMF,
        requestAspectRatio,
        sendConferenceRequest,
        breakout,
        breakoutMoveParticipants,
        joinBreakoutRoom,
        guestLeaveBreakoutRoom,
        closeBreakouts,
        closeBreakoutRoom,
        emptyBreakouts,
        requestParticipants,
        startConference,
        fecc,
        availableLayouts,
        layoutSvgs,
        presInMix,
        breakoutAskForHelp,
        breakoutRemoveAskForHelp,
        notifyNotAFK,
        breakoutRequestGuestToken,
    };
};
